import { createReducer, on } from '@ngrx/store';
import {
  applyActivatedEventsFilter,
  applyGradingReportsFilter,
  applyMyTasksFilter,
  callApi,
  callApiFailure,
  callApiSuccess,
  clearActivatedEventsFilter,
  clearActivatedEventsState,
  clearAllState,
  clearApiCache,
  clearAssignedEventsState,
  clearChecklistState,
  clearCircularCompliancesFilters,
  clearComplianceListFiltersState,
  clearComponentState,
  clearDashboardFiltersState,
  clearDashboardTabState,
  clearGradingReportsFilter,
  clearGradingReportsState,
  clearLicenseState,
  clearMyTasksDashboardState,
  clearMyTasksFilter,
  clearMyTasksFiltersState,
  clearMyTasksRegularState,
  clearMyTasksState,
  clearOverdueComplianceState,
  clearStandardReportDateState,
  clearStandardReportLocationState,
  clearStandardReportState,
  clearWorkspaceComplianceFiltersState,
  getDepartmentHeadDashboadFiltersState,
  loadActivatedEventsData,
  loadActivatedEventsDataFailure,
  loadActivatedEventsDataSuccess,
  loadDashboardFiltersData,
  loadDashboardFiltersDataFailure,
  loadDashboardFiltersDataSuccess,
  loadGradingReportsData,
  loadGradingReportsDataFailure,
  loadGradingReportsDataSuccess,
  loadMyTasksData,
  loadMyTasksDataFailure,
  loadMyTasksDataSuccess,
  loadWorkspaceComplianceFiltersState,
  restoreStandardReportLocationState,
  saveActivatedEventsState,
  saveAssignedEventsState,
  saveCircularCompliancesFilters,
  saveComplianceCalenderState,
  saveComplianceListFiltersState,
  saveComponentState,
  saveDashboardFiltersState,
  saveGradingReportsState,
  saveLicenseState,
  saveMyTasksDashboardState,
  saveMyTasksRegularState,
  saveMyTasksState,
  saveOverdueComplianceState,
  saveStandardReportDateState,
  saveStandardReportLocationState,
  saveStandardReportState,
  setDashboardMainTab,
  setDashboardSubTab,
  setDashboardTabState,
  setDepartmentHeadDashboadFiltersState,
  setManagementDashboadFiltersState,
  setMyTasksDashboardTab,
  setMyTasksRegularTab,
  setMyTasksTab,
  setStandardReportTab,
  setWorkspaceComplianceFiltersState,
  setWorkspaceComplianceFiltersStateAsNull,
  updateActivatedEventsFilters,
  updateDashboardFilters,
  updateGradingReportsFilters,
  updateMyTasksFilters
} from './action';

//interface for overdue compliance state
export interface OverdueComplianceState {
  filters: {
    selectedLocation: any[];
    selectedPeriod: string;
    startDate: Date | null;
    endDate: Date | null;
    isStartEndDateDisable: boolean;
    selectedFinancialYear: any;
    selectedRisks: number[];
    selectedAdvUser: any;
    selectedAct: any;
    selectedCategories: any[];
    selectedComplianceId: string;
    selectedLicenseTypes: string[] | null;
    selectedMitigationPlan: any;
    selectedStatuses: string[] | null;
    selectedLabel: string;
  };
  gridData: any[];
  filteredGridData: any[];
  timestamp: number;
}

const emptyFilters: ComplianceListFilters = {
  selectedFinancialYear: 0,
  selectedRisks: [],
  selectedCategories: [],
  selectedAdvUser: null,
  selectedPeriod: 'All',
  startDate: null,
  endDate: null,
  selectedAct: [],
  selectedComplianceId: '',
  selectedLabel: 'Label',
  selectedDepartment: 0,
  selectedLicenseType: [],
  selectedMitigationPlan: -1,
  selectedChartType: null,
  selectedInternalType: 0
};
/**
 * Snapshot of the My Tasks workspace UI state (filters, grid data, and the active tab).
 *
 * Single source of truth for this shape: it backs the NgRx `myTasksState` /
 * `myTasksDashboardState` / `myTasksRegularState` slices and is re-exported as
 * `MyTasksFiltersState` by `MyTasksStateService` (src/app/service/my-tasks-state.service.ts).
 * Several fields are typed `any` because the underlying Syncfusion dropdown selections
 * (title/task/location/role) are loosely typed throughout the component.
 */
export interface MyTasksState {
  filters: {
    selectedCompliances: number;
    selectedStatus: string;
    // any: Syncfusion DropDownTree selection — loosely typed in the component.
    selectedLocations: any;
    selectedType: number;
    selectedSubType: number;
    selectedAct: number;
    selectedCategory: number;
    fromDate: Date | null;
    toDate: Date | null;
    searchTextPerformer: string;
    searchTextReviewer: string;
    searchText: string;
    // any: Dropdown selections — loosely typed in the component.
    selectedTitle: any;
    selectedTask: any;
    selectedRoleForStatus: any;
  };
  // any: grid rows come from untyped API payloads.
  performerGridData: any[];
  reviewerGridData: any[];
  taskgridData: any[];
  originalPerformerGridData: any[];
  originalReviewerGridData: any[];
  originalTaskGridData: any[];
  selectedTabIndex: number;
  isLoading: boolean;
  // any: error payload shape is not constrained.
  error: any;
  timestamp: number;
}
/**
 * Default My Tasks filter selection used whenever a My Tasks slice is created without a
 * previous filter state (dashboard, regular and legacy slices all start from it). Frozen so
 * that sharing one reference between slices can never leak a mutation across them.
 */
export const DEFAULT_MY_TASKS_FILTERS: Readonly<MyTasksState['filters']> = Object.freeze({
  selectedCompliances: 1,
  selectedStatus: "Status",
  selectedLocations: null,
  selectedType: -1,
  selectedSubType: -1,
  selectedAct: -1,
  selectedCategory: -1,
  fromDate: null,
  toDate: null,
  searchTextPerformer: '',
  searchTextReviewer: '',
  searchText: '',
  selectedTitle: null,
  selectedTask: null,
  selectedRoleForStatus: null
});
/**
 * My Tasks filter fields cleared by "clear filters" on the Performer and Reviewer tabs
 * (everything except each tab's own search text, which the reducer resets separately).
 */
const MY_TASKS_COMPLIANCE_FILTER_RESET: Partial<MyTasksState['filters']> = {
  selectedLocations: null,
  selectedStatus: "Status",
  selectedType: -1,
  selectedSubType: -1,
  selectedAct: -1,
  selectedCategory: -1,
  fromDate: null,
  toDate: null
};
export interface ComponentState<TFilters = any, TGrid = any> {
  filters: TFilters;
  gridData: TGrid[];
  timestamp: number; // For freshness check
}

// Dashboard Tab State Interface
export interface DashboardTabState {
  mainTabIndex: number;
  subTabIndex: number;
  timestamp: number;
}

// Activated Events State Interface
export interface ActivatedEventsState {
  filters: {
    selectedEventType: number | null;
    selectedLocation: any[];
    filterText: string;
    selectedRole: number | null;
  };
  gridData: any[];
  originalGridData: any[];
  isLoading: boolean;
  error: any;
  timestamp: number;
}

export interface ComplianceCalenderState {
  filters: {
    selectedType: string;
    selectedDate: Date;
  };
  calendarData: any;
  gridData: any[];
  role: string;
  timestamp?: number;
}

export interface StandardReportState {
  selectedTabIndex: number;
  selectedLocation: any;
  locationDropdownData: any[];
  showLocationDropdown: boolean;
  clParameterValue: string;
  startDate: Date | null;
  endDate: Date | null;
  timestamp: number;
}

export interface DashboardFiltersState {
  filters: {
    selectedLocation: any[];
    selectedSubUnit: number;
    selectedSubUnitText: string;
    selectedPeriod: number;
    selectedPeriodText: string;
    fromDate: Date | null;
    toDate: Date | null;
    enableCustomDates: boolean;
    locationList?: any[];
  };
  isLoading: boolean;
  error: any;
  timestamp: number;
}

/**
 * The Circular Compliances tab's applied filter selection, as the tab saves it on every filter
 * application: the ids its Regulator / Circular No. dropdowns hold (null for "every"), the
 * period code the range was seeded from and the Start/End bounds themselves in the endpoints'
 * `yyyy-MM-ddT00:00:00` shape (null for no bound). The bounds are stored explicitly, not
 * re-derived from the period, so a hand-edited date comes back exactly as it was applied.
 */
export interface CircularCompliancesFilters {
  regulator_id: number | null;
  circular_id: number | null;
  period: number;
  from_date: string | null;
  to_date: string | null;
}

/** The saved Circular Compliances filters plus when they were saved. */
export interface CircularCompliancesFiltersState extends CircularCompliancesFilters {
  timestamp: number;
}

export interface departmentHeadDashboadFilterState {
  selectedLocation: any[];
  selectedComplianceTypeValue: number;
  selectedComplianceTypeName: string;
  selectedPeriodValue: number;
  selectedPeriodName: string;
  fromDate: Date | null;
  toDate: Date | null;
  enableCustomDates: boolean;
  locationList?: any[];
}

export interface managementDashboadFilterState {  
  fromDate: Date | null;
  toDate: Date | null;
}

// Grading Reports State Interface
export interface GradingReportsState {
  filters: {
    selectedLocation: any[];
    selectedFinancialYear: any;
    selectedMonth: number | null;
    selectedPeriod: number | null;
  };
  gridData: any[];
  dynamicColumns: any[];
  isLoading: boolean;
  error: any;
  timestamp: number;
}

export interface ComplianceListFilters {
  selectedFinancialYear: number;
  selectedRisks: any[];         // ✅ was any[]
  selectedCategories: number[];      // ✅ was any[]
  selectedAdvUser: any | null;
  selectedPeriod: string;   // ✅ accept both, safer
  startDate: Date | null;
  endDate: Date | null;
  selectedAct: any[];
  selectedComplianceId: string;
  selectedLabel: string;
  selectedDepartment: number;
  selectedLicenseType: any[];
  selectedMitigationPlan: number;
  selectedChartType: any | null;
  selectedInternalType: number;
}

export interface ComplianceListState {
  filters: ComplianceListFilters;
  gridData: any[];
  filteredGridData: any[];
  selectedTabIndex: number;
  isLoading: boolean;
  error: any;
  timestamp: number;
}
/** One recorded `callApiFailure` for a request key; see {@link ApiState.failures}. */
export interface ApiFailureEntry {
  /** The error the effect caught (HttpErrorResponse, timeout error, ...). */
  // any: the effect forwards whatever the HTTP pipeline threw, untyped.
  error: any;
}

export interface ApiState {
  loading: boolean;
  error: any;
  data: { [key: string]: any };
  /**
   * Latest `callApiFailure` per request key. A new object is stored on every failure, so a
   * caller waiting on a key can tell a fresh failure from an older one by reference. Read by
   * `awaitApiStoreResult` (src/app/service/api-store-request.ts). Optional so existing
   * ApiState literals (tests, fixtures) stay valid.
   */
  failures?: { [key: string]: ApiFailureEntry };
  overdueCompliance: OverdueComplianceState | null;
  myTasksState: MyTasksState | null;
  myTasksDashboardState: MyTasksState | null;
  myTasksRegularState: MyTasksState | null;
  dashboardTabState: DashboardTabState | null;
  activatedEventsState: ActivatedEventsState | null;
  assignedEventsState: ActivatedEventsState | null;
  complianceCalenderState: ComplianceCalenderState | null;
  gradingReportsState: GradingReportsState | null;
  dashboardFiltersState: DashboardFiltersState | null;
  circularCompliancesFiltersState: CircularCompliancesFiltersState | null;
  departmentHeadDashboadFilterState: departmentHeadDashboadFilterState | null;
  managementDashboadFilterState: managementDashboadFilterState | null;
  myComplianceListState: ComplianceListState | null;
  workspaceComplianceState: workspaceComplianceState | null;
  standardReportState: StandardReportState | null;
  checklistState: ChecklistState | null;
  licenseState: LicenseState | null;
  lockingDetailsReportState: LockingDetailsReportState | null;
}

export interface ChecklistState {
  filters: {
    selectedUser: any;
    selectedLocation: any[];
    selectedComplianceType: any;
    selectedStatus: any;
    selectedRisks: any[];
    complianceId: any;
    selectedPeriodID: any;
    fromDate: Date | null;
    toDate: Date | null;
    selectedDocTypes: any;
    selectedMitigationPlan: any;
    selectedAct: any;
    selectedAdvDepartment: any;
    selectedFinancialYear: any;
    selectedAdvUser: any;
    selectedLabel: any;
    selectedEventName: any;
    selectedEventNature: any;
  };
  gridData: any[];
  timestamp?: number;
}

export interface workspaceComplianceState {
  selectedUser: number;
  locationArrayString: number[];
  selectedComplianceType: number[];
  selectedRiskStatus: any;
  riskArrayString: number[];
  compliaceID: string;
  selectedPeriodID: any;
  fromDate: Date | null;
  toDate: Date | null;
  selectedDocStatus: any;
  selectedMitigationPlan: any;
  selectedAct: any;
  selectedAdvDepartment: any;
  selectedFinancialYear: any;
  userArrayString: number[];
  sta_para: any;
  selectedEventName: string;
  selectedEventNature: string
}

export interface LicenseState {
  filters: {
    selectedStatutoryType: any;
    selectedLocation: any;
    selectedValue: any;
    selectedStatus: any;
    selectedDocumentStatus: any;
  };
  gridData: any[];
  selectedTab: string;
  timestamp?: number;
}

export interface LockingDetailsReportState {
  filters: {
    selectedComplianceType: string | null;
    selectedAct: number;
    selectedCompliance: number | null;
    selectedComplianceId: string;
  };
  gridData: any[];
  timestamp?: number;
}

export const initialState: ApiState = {
  loading: false,
  error: null,
  data: {},
  overdueCompliance: null,
  myTasksState: null,
  myTasksDashboardState: null,
  myTasksRegularState: null,
  dashboardTabState: null,
  activatedEventsState: null,
  assignedEventsState: null,
  complianceCalenderState: null,
  gradingReportsState: null,
  dashboardFiltersState: null,
  circularCompliancesFiltersState: null,
  myComplianceListState: null,
  workspaceComplianceState: null,
  standardReportState: null,
  departmentHeadDashboadFilterState: null,
  managementDashboadFilterState: null,
  checklistState: null,
  licenseState: null,
  lockingDetailsReportState: null
};



/**
 * Builds the My Tasks slice after a tab switch: the existing slice with the new
 * tab index, or, when there is no saved slice yet, a fresh default slice (all
 * filters at their defaults, empty grids) opened on that tab. Shared by the
 * regular, dashboard and plain My Tasks tab actions.
 *
 * Pure: never mutates `current`, and builds a new default object (new
 * `filters` and new arrays) on every call so no two slices share references.
 * @param current - The slice currently in the store, or `null` when none is saved.
 * @param tabIndex - The newly selected tab index.
 * @returns A new `MyTasksState` stamped with the current time.
 */
function myTasksStateWithTab(current: MyTasksState | null, tabIndex: number): MyTasksState {
  if (current) {
    return {
      ...current,
      selectedTabIndex: tabIndex,
      timestamp: Date.now()
    };
  }
  return newMyTasksState(defaultMyTasksFilters(), tabIndex, false);
}

/**
 * The My Tasks filters at their defaults (all compliances, no status/location/type/act/category,
 * no dates, empty search texts). Returns a new object on every call.
 * @returns A fresh default filters object.
 */
function defaultMyTasksFilters(): MyTasksState['filters'] {
  return {
    selectedCompliances: 1,
    selectedStatus: "Status",
    selectedLocations: null,
    selectedType: -1,
    selectedSubType: -1,
    selectedAct: -1,
    selectedCategory: -1,
    fromDate: null,
    toDate: null,
    searchTextPerformer: '',
    searchTextReviewer: '',
    searchText: '',
    selectedTitle: null,
    selectedTask: null,
    selectedRoleForStatus: null
  };
}

/**
 * A new My Tasks slice with the given filters, empty grids (new arrays on every call), no error,
 * stamped with the current time.
 * @param filters - The slice's filters (used as-is).
 * @param selectedTabIndex - The selected tab.
 * @param isLoading - Whether a load is in flight.
 * @returns The new slice.
 */
function newMyTasksState(filters: MyTasksState['filters'], selectedTabIndex: number, isLoading: boolean): MyTasksState {
  return {
    filters,
    performerGridData: [],
    reviewerGridData: [],
    taskgridData: [],
    originalPerformerGridData: [],
    originalReviewerGridData: [],
    originalTaskGridData: [],
    selectedTabIndex,
    isLoading,
    error: null,
    timestamp: Date.now()
  };
}

export const apiReducer = createReducer(
  initialState,

  on(callApi, (state) => ({ ...state, loading: true, error: null })),

  // Clear the entire store when clearAllState is dispatched
  on(clearAllState, () => ({ ...initialState })),

  // A success also drops the key's failure entry, so `failures` only holds keys whose latest
  // attempt failed instead of accumulating one entry per key ever attempted.
  on(callApiSuccess, (state, { key, data }) => {
    const { [key]: _resolved, ...failures } = state.failures ?? {};
    return {
      ...state,
      loading: false,
      error: null,
      data: { ...state.data, [key]: data },
      failures,
    };
  }),

  on(callApiFailure, (state, { key, error }) => ({
    ...state,
    loading: false,
    error,
    failures: { ...state.failures, [key]: { error } },
  })),

  on(saveOverdueComplianceState, (state, { state: overdueState }) => ({
    ...state,
    overdueCompliance: {
      ...overdueState,
      timestamp: Date.now()
    }
  })),

  on(clearOverdueComplianceState, (state) => ({
    ...state,
    overdueCompliance: null
  })),

  // MyTasks state reducers
  on(saveMyTasksState, (state, { state: myTasksState }) => ({
    ...state,
    myTasksState: {
      ...myTasksState,
      timestamp: Date.now()
    }
  })),

  on(clearMyTasksState, (state) => ({
    ...state,
    myTasksState: null
  })),

  // Handle tab change action
  on(setMyTasksTab, (state, { tabIndex }) => ({
    ...state,
    myTasksState: myTasksStateWithTab(state.myTasksState, tabIndex)
  })),

  on(saveComponentState, (state, { componentKey, state: componentState }) => ({
    ...state,
    data: {
      ...state.data,
      [componentKey]: {
        ...componentState,
        timestamp: Date.now()
      }
    },
  })),

  on(clearComponentState, (state, { componentKey }) => {
    const nextData = { ...state.data };
    delete nextData[componentKey];
    return {
      ...state,
      data: nextData
    };
  }),

  // Dashboard Tab State Reducers
  on(setDashboardMainTab, (state, { mainTabIndex }) => ({
    ...state,
    dashboardTabState: {
      mainTabIndex,
      subTabIndex: state.dashboardTabState?.subTabIndex ?? 0,
      timestamp: Date.now()
    }
  })),

  on(setDashboardSubTab, (state, { subTabIndex }) => ({
    ...state,
    dashboardTabState: {
      mainTabIndex: state.dashboardTabState?.mainTabIndex ?? 0,
      subTabIndex,
      timestamp: Date.now()
    }
  })),

  on(setDashboardTabState, (state, { mainTabIndex, subTabIndex, timestamp }) => ({
    ...state,
    dashboardTabState: {
      mainTabIndex,
      subTabIndex,
      timestamp: timestamp || Date.now()
    }
  })),

  on(clearDashboardTabState, (state) => ({
    ...state,
    dashboardTabState: null
  })),

  // Separate MyTasks state reducers for dashboard vs regular navigation
  on(saveMyTasksDashboardState, (state, { state: myTasksState }) => ({
    ...state,
    myTasksDashboardState: {
      ...myTasksState,
      timestamp: Date.now()
    }
  })),

  on(clearMyTasksDashboardState, (state) => ({
    ...state,
    myTasksDashboardState: null
  })),

  on(saveMyTasksRegularState, (state, { state: myTasksState }) => ({
    ...state,
    myTasksRegularState: {
      ...myTasksState,
      timestamp: Date.now()
    }
  })),

  on(clearMyTasksRegularState, (state) => ({
    ...state,
    myTasksRegularState: null
  })),

  on(setMyTasksDashboardTab, (state, { tabIndex }) => ({
    ...state,
    myTasksDashboardState: myTasksStateWithTab(state.myTasksDashboardState, tabIndex)
  })),

  on(setMyTasksRegularTab, (state, { tabIndex }) => ({
    ...state,
    myTasksRegularState: myTasksStateWithTab(state.myTasksRegularState, tabIndex)
  })),

  // Activated Events State Reducers
  on(saveActivatedEventsState, (state, { state: activatedEventsState }) => ({
    ...state,
    activatedEventsState: {
      ...activatedEventsState,
      timestamp: Date.now()
    }
  })),

  on(clearActivatedEventsState, (state) => ({
    ...state,
    activatedEventsState: null
  })),

  on(loadActivatedEventsData, (state) => ({
    ...state,
    activatedEventsState: {
      filters: state.activatedEventsState?.filters || {
        selectedEventType: null,
        selectedLocation: [],
        filterText: '',
        selectedRole: null
      },
      gridData: [],
      originalGridData: [],
      isLoading: true,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadActivatedEventsDataSuccess, (state, { data }) => ({
    ...state,
    activatedEventsState: {
      ...state.activatedEventsState!,
      gridData: data,
      originalGridData: data,
      isLoading: false,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadActivatedEventsDataFailure, (state, { error }) => ({
    ...state,
    activatedEventsState: {
      ...state.activatedEventsState!,
      isLoading: false,
      error,
      timestamp: Date.now()
    }
  })),

  on(updateActivatedEventsFilters, (state, { filters }) => ({
    ...state,
    activatedEventsState: state.activatedEventsState ? {
      ...state.activatedEventsState,
      filters: {
        ...state.activatedEventsState.filters,
        ...filters
      },
      timestamp: Date.now()
    } : {
      filters: {
        selectedEventType: null,
        selectedLocation: [],
        filterText: '',
        ...filters
      },
      gridData: [],
      originalGridData: [],
      isLoading: false,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(applyActivatedEventsFilter, (state) => {
    if (!state.activatedEventsState) return state;

    const { filters, originalGridData } = state.activatedEventsState;
    const filteredData = originalGridData.filter(item => {
      const matchesEventType = filters.selectedEventType ?
        item.event_classification_id === filters.selectedEventType : false;
      const matchesLocation = filters.selectedLocation && filters.selectedLocation.length > 0 ?
        filters.selectedLocation.includes(String(item.customer_branch_id)) : false;
      const matchesFilterText = filters.filterText.trim() ?
        [item.name, item.customer_branch_name, item.user_name, item.event_description]
          .some(field => field && field.includes(filters.filterText)) : false;

      return matchesEventType || matchesLocation || matchesFilterText;
    });

    return {
      ...state,
      activatedEventsState: {
        ...state.activatedEventsState,
        gridData: filteredData,
        timestamp: Date.now()
      }
    };
  }),

  on(clearActivatedEventsFilter, (state) => {
    if (!state.activatedEventsState) return state;

    return {
      ...state,
      activatedEventsState: {
        ...state.activatedEventsState,
        filters: {
          selectedEventType: null,
          selectedLocation: [],
          filterText: '',
          selectedRole: null
        },
        gridData: state.activatedEventsState.originalGridData,
        timestamp: Date.now()
      }
    };
  }),

  // Assigned Events State Reducers (mirror of Activated Events)
  on(saveAssignedEventsState, (state, { state: assignedState }) => ({
    ...state,
    assignedEventsState: {
      ...assignedState,
      timestamp: Date.now()
    }
  })),

  on(clearAssignedEventsState, (state) => ({
    ...state,
    assignedEventsState: null
  })),

  on(saveComplianceListFiltersState, (state, { state: newState }) => ({
    ...state,
    myComplianceListState: newState
  })),


  on(clearComplianceListFiltersState, (state) => ({
    ...state,
    myComplianceListState: null
    // filters: emptyFilters,
    // timestamp: Date.now()
  })),

  on(clearMyTasksFiltersState, (state) => ({
    ...state,
    myTasksState: null
  })),

  on(loadMyTasksData, (state) => ({
    ...state,
    myTasksState: newMyTasksState(state.myTasksState?.filters || defaultMyTasksFilters(), 0, true)
  })),

  on(loadMyTasksDataSuccess, (state, { data, tabType }) => {
    if (!state.myTasksState) return state;

    const updateData = (gridData: any[], originalGridData: any[]) => ({
      gridData: [...data],
      originalGridData: [...data]
    });

    let updates: any = {};

    if (tabType === 'performer') {
      updates = updateData(state.myTasksState.performerGridData, state.myTasksState.originalPerformerGridData);
    } else if (tabType === 'reviewer') {
      updates = updateData(state.myTasksState.reviewerGridData, state.myTasksState.originalReviewerGridData);
    } else if (tabType === 'task') {
      updates = updateData(state.myTasksState.taskgridData, state.myTasksState.originalTaskGridData);
    }

    return {
      ...state,
      myTasksState: {
        ...state.myTasksState,
        ...updates,
        isLoading: false,
        error: null,
        timestamp: Date.now()
      }
    };
  }),

  on(loadMyTasksDataFailure, (state, { error, tabType }) => ({
    ...state,
    myTasksState: state.myTasksState ? {
      ...state.myTasksState,
      isLoading: false,
      error,
      timestamp: Date.now()
    } : null
  })),

  on(updateMyTasksFilters, (state, { filters }) => ({
    ...state,
    myTasksState: state.myTasksState ? {
      ...state.myTasksState,
      filters: {
        ...state.myTasksState.filters,
        ...filters
      },
      timestamp: Date.now()
    } : newMyTasksState({ ...defaultMyTasksFilters(), ...filters }, 0, false)
  })),

  on(applyMyTasksFilter, (state, { tabType }) => {
    if (!state.myTasksState) return state;

    const { filters, originalPerformerGridData, originalReviewerGridData, originalTaskGridData } = state.myTasksState;

    let filteredData: any[] = [];

   const dataMap: Record<string, any[]> = {
  performer: originalPerformerGridData,
  reviewer: originalReviewerGridData,
  task: originalTaskGridData
};
const originalData = dataMap[tabType] || [];
/**
 * Filters compliance data by location, status, type, subtype, act, category, date range, and search text based on the active tab.
 * @example
 * filterComplianceData(data, filters, "performer")
 * filteredData
 * @param {any[]} data - Array of compliance items to filter.
 * @param {any} filters - Filter criteria object containing selected values and search text.
 * @param {string} tabType - Active tab type used to determine which search text field to apply.
 * @returns {any[]} Filtered array of compliance items that match all applied criteria.
 **/
function filterComplianceData(data: any[], filters: any, tabType: string) {
  return data.filter(item => {
    const searchText =
      tabType === 'performer'
        ? filters.searchTextPerformer
        : filters.searchTextReviewer;

    const matchesLocation = (() => {
      if (!filters.selectedLocations) return true;
      
      if (Array.isArray(filters.selectedLocations)) {
        return filters.selectedLocations.length === 0 || 
               filters.selectedLocations.includes(item.customer_branch_id?.toString());
      }
      return String(item.customer_branch_id) === String(filters.selectedLocations);
    })();

    const matchesStatus =
      !filters.selectedStatus ||
      filters.selectedStatus === "Status" ||
      item.status === filters.selectedStatus;

    const matchesType =
      !filters.selectedType ||
      filters.selectedType === -1 ||
      item.com_type_id === filters.selectedType;

    const matchesSubType =
      !filters.selectedSubType ||
      filters.selectedSubType === -1 ||
      item.sub_type_id === filters.selectedSubType;

    const matchesAct =
      !filters.selectedAct ||
      filters.selectedAct === -1 ||
      item.act_id === filters.selectedAct;

    const matchesCategory =
      !filters.selectedCategory ||
      filters.selectedCategory === -1 ||
      item.com_category === filters.selectedCategory;

    const matchesDateRange =
      !filters.fromDate ||
      !filters.toDate ||
      (item.scheduled_on >= filters.fromDate &&
        item.scheduled_on <= filters.toDate);

    const matchesSearch =
      !searchText ||
      item.task_title?.toLowerCase().includes(searchText.toLowerCase());

    return (
      matchesLocation &&
      matchesStatus &&
      matchesType &&
      matchesSubType &&
      matchesAct &&
      matchesCategory &&
      matchesDateRange &&
      matchesSearch
    );
  });
}
/**
* Filters task data by selected title, selected task type, and search text.
* @example
* filterTaskData(data, filters)
* filteredTaskData
* @param {any[]} data - The array of task items to filter.
* @param {any} filters - The filter criteria object containing selectedTitle, selectedTask, and searchText.
* @returns {any[]} The filtered array of task items.
**/
function filterTaskData(data: any[], filters: any) {
  return data.filter(item => {
    return (
      (!filters.selectedTitle ||
        item.ID === filters.selectedTitle) &&
      (!filters.selectedTask ||
        item.TaskType === filters.selectedTask) &&
      (!filters.searchText ||
        item.Title?.toLowerCase().includes(filters.searchText.toLowerCase()) ||
        item.Description?.toLowerCase().includes(filters.searchText.toLowerCase()))
    );
  });
}
    // Apply filters based on tab type
   filteredData =
  tabType === 'task'
    ? filterTaskData(originalData, filters)
    : filterComplianceData(originalData, filters, tabType);

    const updateMap: Record<string, any> = {
    performer: { performerGridData: filteredData },
    reviewer: { reviewerGridData: filteredData },
    task: { taskgridData: filteredData }
  };

    return {
    ...state,
    myTasksState: {
      ...state.myTasksState,
      ...updateMap[tabType],
      timestamp: Date.now()
    }
  };
  }),

  on(clearMyTasksFilter, (state, { tabType }) => {
    if (!state.myTasksState) return state;

    let updates: any = {};

    if (tabType === 'performer') {
      updates = {
        performerGridData: [...state.myTasksState.originalPerformerGridData],
        filters: {
          ...state.myTasksState.filters,
          ...MY_TASKS_COMPLIANCE_FILTER_RESET,
          searchTextPerformer: ''
        }
      };
    } else if (tabType === 'reviewer') {
      updates = {
        reviewerGridData: [...state.myTasksState.originalReviewerGridData],
        filters: {
          ...state.myTasksState.filters,
          ...MY_TASKS_COMPLIANCE_FILTER_RESET,
          searchTextReviewer: ''
        }
      };
    } else if (tabType === 'task') {
      updates = {
        taskgridData: [...state.myTasksState.originalTaskGridData],
        filters: {
          ...state.myTasksState.filters,
          selectedTitle: null,
          selectedTask: null,
          searchText: ''
        }
      };
    }

    return {
      ...state,
      myTasksState: {
        ...state.myTasksState,
        ...updates,
        timestamp: Date.now()
      }
    };
  }),


  on(saveComplianceCalenderState, (state, { state: saveComplianceCalenderState }) => ({
    ...state,
    complianceCalenderState: {
      ...saveComplianceCalenderState,
      timestamp: Date.now()
    }
  })),

  // Grading Reports State Reducers
  on(saveGradingReportsState, (state, { state: gradingReportsState }) => ({
    ...state,
    gradingReportsState: {
      ...gradingReportsState,
      timestamp: Date.now()
    }
  })),

  on(clearGradingReportsState, (state) => ({
    ...state,
    gradingReportsState: null
  })),

  on(updateGradingReportsFilters, (state, { filters }) => ({
    ...state,
    gradingReportsState: state.gradingReportsState ? {
      ...state.gradingReportsState,
      filters: {
        ...state.gradingReportsState.filters,
        ...filters
      },
      timestamp: Date.now()
    } : {
      filters: {
        selectedLocation: [],
        selectedFinancialYear: null,
        selectedMonth: null,
        selectedPeriod: null,
        ...filters
      },
      gridData: [],
      dynamicColumns: [],
      isLoading: false,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadGradingReportsData, (state) => ({
    ...state,
    gradingReportsState: {
      filters: state.gradingReportsState?.filters || {
        selectedLocation: [],
        selectedFinancialYear: null,
        selectedMonth: null,
        selectedPeriod: null
      },
      gridData: [],
      dynamicColumns: [],
      isLoading: true,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadGradingReportsDataSuccess, (state, { data, dynamicColumns }) => ({
    ...state,
    gradingReportsState: {
      ...state.gradingReportsState!,
      gridData: data,
      dynamicColumns: dynamicColumns,
      isLoading: false,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadGradingReportsDataFailure, (state, { error }) => ({
    ...state,
    gradingReportsState: {
      ...state.gradingReportsState!,
      isLoading: false,
      error,
      timestamp: Date.now()
    }
  })),

  on(applyGradingReportsFilter, (state) => {
    if (!state.gradingReportsState) return state;

    const { filters, gridData } = state.gradingReportsState;

    // Apply location filter if selected
    let filteredData = gridData;
    if (filters.selectedLocation && filters.selectedLocation.length > 0) {
      filteredData = gridData.filter((item: any) =>
        filters.selectedLocation.includes(item.customer_branch_id)
      );
    }

    return {
      ...state,
      gradingReportsState: {
        ...state.gradingReportsState,
        gridData: filteredData,
        timestamp: Date.now()
      }
    };
  }),

  on(clearGradingReportsFilter, (state) => {
    if (!state.gradingReportsState) return state;

    return {
      ...state,
      gradingReportsState: {
        ...state.gradingReportsState,
        filters: {
          selectedLocation: [],
          selectedFinancialYear: null,
          selectedMonth: null,
          selectedPeriod: null
        },
        timestamp: Date.now()
      }
    };
  }),

  // Dashboard Filters State Reducers
  on(saveDashboardFiltersState, (state, { state: dashboardFiltersState }) => ({
    ...state,
    dashboardFiltersState: {
      ...dashboardFiltersState,
      timestamp: Date.now()
    }
  })),

  on(clearDashboardFiltersState, (state) => ({
    ...state,
    dashboardFiltersState: null
  })),

  // Circular Compliances tab filter persistence: the whole selection is replaced on every save,
  // never merged, so a cleared dropdown (null) is stored as cleared.
  on(saveCircularCompliancesFilters, (state, { filters }) => ({
    ...state,
    circularCompliancesFiltersState: {
      ...filters,
      timestamp: Date.now()
    }
  })),

  on(clearCircularCompliancesFilters, (state) => ({
    ...state,
    circularCompliancesFiltersState: null
  })),

  on(updateDashboardFilters, (state, { filters }) => ({
    ...state,
    dashboardFiltersState: state.dashboardFiltersState ? {
      ...state.dashboardFiltersState,
      filters: {
        ...state.dashboardFiltersState.filters,
        ...filters
      },
      timestamp: Date.now()
    } : {
      filters: {
        selectedLocation: [],
        selectedSubUnit: 2,
        selectedSubUnitText: 'Statutory',
        selectedPeriod: 13,
        selectedPeriodText: 'Current + Previous YTD',
        fromDate: null,
        toDate: null,
        enableCustomDates: false,
        locationList: [],
        ...filters
      },
      isLoading: false,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadDashboardFiltersData, (state) => ({
    ...state,
    dashboardFiltersState: {
      filters: state.dashboardFiltersState?.filters || {
        selectedLocation: [],
        selectedSubUnit: 2,
        selectedSubUnitText: 'Statutory',
        selectedPeriod: 13,
        selectedPeriodText: 'Current + Previous YTD',
        fromDate: null,
        toDate: null,
        enableCustomDates: false,
        locationList: []
      },
      isLoading: true,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadDashboardFiltersDataSuccess, (state, { locationList }) => ({
    ...state,
    dashboardFiltersState: {
      ...state.dashboardFiltersState!,
      filters: {
        ...state.dashboardFiltersState!.filters,
        locationList: locationList
      },
      isLoading: false,
      error: null,
      timestamp: Date.now()
    }
  })),

  on(loadDashboardFiltersDataFailure, (state, { error }) => ({
    ...state,
    dashboardFiltersState: {
      ...state.dashboardFiltersState!,
      isLoading: false,
      error,
      timestamp: Date.now()
    }
  })),

  // load --- for when we first land on page

  on(loadWorkspaceComplianceFiltersState, (state, { state: workspaceState }) => ({
    ...state,
    workspaceComplianceState: workspaceState,
  })),

  // set --- for when we click on apply btn

  on(setWorkspaceComplianceFiltersState, (state, { state: workspaceState }) => ({
    ...state,
    workspaceComplianceState: workspaceState
  })),

  // clear --- for when we click on clear btn

  on(clearWorkspaceComplianceFiltersState, (state, { state: workspaceSate }) => ({
    ...state,
    workspaceComplianceState: workspaceSate
  })),

  // set to null --- when we land on dashboard

  on(setWorkspaceComplianceFiltersStateAsNull, (state) => ({
    ...state,
    workspaceComplianceState: null,
    timestamp: Date.now()
  })),

  on(setStandardReportTab, (state, { tabIndex }) => ({
    ...state,
    standardReportState: state.standardReportState ? {
      ...state.standardReportState,
      selectedTabIndex: tabIndex,
      timestamp: Date.now()
    } : {
      selectedTabIndex: tabIndex,
      selectedLocation: null,
      locationDropdownData: [],
      showLocationDropdown: false,
      clParameterValue: 'C',
      startDate: null,
      endDate: null,
      timestamp: Date.now()
    }
  })),

  on(saveStandardReportState, (state, { state: standardReportState }) => ({
    ...state,
    standardReportState: {
      ...standardReportState,
      timestamp: Date.now()
    }
  })),

  on(clearStandardReportState, (state) => ({
    ...state,
    standardReportState: null
  })),

  on(saveStandardReportLocationState, (state, { selectedLocation, locationDropdownData, showLocationDropdown, clParameterValue, startDate, endDate }) => ({
    ...state,
    standardReportState: state.standardReportState ? {
      ...state.standardReportState,
      selectedLocation,
      locationDropdownData,
      showLocationDropdown,
      clParameterValue,
      startDate: startDate || state.standardReportState.startDate,
      endDate: endDate || state.standardReportState.endDate,
      timestamp: Date.now()
    } : {
      selectedTabIndex: 0,
      selectedLocation,
      locationDropdownData,
      showLocationDropdown,
      clParameterValue,
      startDate: startDate || null,
      endDate: endDate || null,
      timestamp: Date.now()
    }
  })),

  on(clearStandardReportLocationState, (state) => ({
    ...state,
    standardReportState: state.standardReportState ? {
      ...state.standardReportState,
      selectedLocation: null,
      timestamp: Date.now()
    } : null
  })),

  on(restoreStandardReportLocationState, (state) => ({
    ...state,
    standardReportState: state.standardReportState ? {
      ...state.standardReportState,
      timestamp: Date.now()
    } : null
  })),

  // Standard Report Date State Reducers
  on(saveStandardReportDateState, (state, { startDate, endDate }) => ({
    ...state,
    standardReportState: state.standardReportState ? {
      ...state.standardReportState,
      startDate,
      endDate,
      timestamp: Date.now()
    } : {
      selectedTabIndex: 0,
      selectedLocation: null,
      locationDropdownData: [],
      showLocationDropdown: false,
      clParameterValue: 'C',
      startDate,
      endDate,
      timestamp: Date.now()
    }
  })),

  on(clearStandardReportDateState, (state) => ({
    ...state,
    standardReportState: state.standardReportState ? {
      ...state.standardReportState,
      startDate: null,
      endDate: null,
      timestamp: Date.now()
    } : null
  })),

  // set --- for setting values of dashboad into redux store when we click on apply btn or load the filter component

  on(setDepartmentHeadDashboadFiltersState, (state, { state: departmentHeadDashboadFilterState }) => ({
    ...state,
    departmentHeadDashboadFilterState: departmentHeadDashboadFilterState
  })),

  // get --- for retrieving values of dashboard filters from redux wherever and whenever required

  on(getDepartmentHeadDashboadFiltersState, (state, { state: departmentHeadDashboadFilterState }) => ({
    ...state,
    departmentHeadDashboadFilterState: departmentHeadDashboadFilterState
  })),

  // set --- for setting values of dashboad into redux store when we click on apply btn or load the filter component

  on(setManagementDashboadFiltersState, (state, { state: managementDashboadFilterState }) => ({
    ...state,
    managementDashboadFilterState: managementDashboadFilterState
  })),

  // get --- for retrieving values of dashboard filters from redux wherever and whenever required

  // No getManagementDashboadFiltersState action exists; removed erroneous handler


  on(clearApiCache, (state, { key }) => {
    const newData = { ...state.data };
    delete newData[key];
    return {
      ...state,
      data: newData,
    };
  }),

  // Checklist State Management
  on(clearChecklistState, (state) => ({
    ...state,
    checklistState: null
  })),

  on(saveLicenseState, (state, { state: licenseState }) => ({
    ...state,
    licenseState: {
      ...licenseState,
      timestamp: Date.now()
    }
  })),

  on(clearLicenseState, (state) => ({
    ...state,
    licenseState: null
  })),

);


